import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Plus,
  Search,
  MoreHorizontal,
  CheckCircle,
  Truck,
  Package,
  Clock,
  DollarSign,
  FileText,
  Trash2,
  Eye,
  Receipt,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Printer,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import DocumentViewer from '../components/DocumentViewer';
import axios from 'axios';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency display helper
const CurrencyValue = ({ value, symbol, isSAR, size = 14 }) => {
  if (isSAR) {
    return (
      <span className="inline-flex items-center gap-1">
        <SARSymbol size={size} />
        <span>{value}</span>
      </span>
    );
  }
  return <span>{symbol} {value}</span>;
};

const SalesOrders = () => {
  const navigate = useNavigate();
  const { currencySymbol, formatCurrency, isSAR } = useSettings();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [template, setTemplate] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, shipped: 0, delivered: 0, awaiting_invoice: 0, total_value: 0 });
  const { toast: showToast } = useToast();
  
  const [formData, setFormData] = useState({
    client_id: '',
    currency: 'USD',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    expected_delivery: '',
    shipping_address: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchClients();
    fetchProducts();
    fetchStats();
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/templates/default?document_type=sales_order`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(res.data);
    } catch (err) {
      console.log('No template found, using defaults');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/sales/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (err) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/crm/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (err) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/procurement/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/sales/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.orders);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return { subtotal, tax_amount: subtotal * 0.1, total: subtotal * 1.1 };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length ? newItems : [{ description: '', quantity: 1, unit_price: 0, total: 0 }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totals = calculateTotals(formData.items);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/orders`, {
        ...formData,
        ...totals,
        status: 'confirmed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sales order created successfully');
      setDialogOpen(false);
      fetchOrders();
      fetchStats();
      resetForm();
    } catch (err) {
      toast.error('Failed to create order');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      currency: 'USD',
      items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      expected_delivery: '',
      shipping_address: '',
      notes: ''
    });
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/sales/orders/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order marked as ${status}`);
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const markAsDelivered = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/orders/${id}/deliver`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order marked as delivered');
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error('Failed to mark as delivered');
    }
  };

  const createInvoice = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/sales/orders/${id}/create-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast({
        title: "Invoice Created!",
        description: `Invoice ${res.data.invoice.invoice_number} has been created.`,
      });
      setCreateInvoiceDialogOpen(false);
      setViewDialogOpen(false);
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/sales/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted');
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      confirmed: 'bg-blue-100 text-blue-700',
      processing: 'bg-amber-100 text-amber-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
      invoiced: 'bg-indigo-100 text-indigo-700'
    };
    const labels = {
      draft: 'Draft',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      invoiced: 'Invoiced'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  // Count orders awaiting invoice (delivered but no invoice)
  const awaitingInvoiceCount = orders.filter(o => o.status === 'delivered' && !o.invoice_id).length;

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          o.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Orders</h1>
          <p className="text-muted-foreground">Manage customer orders and fulfillment</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> New Order
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-muted-foreground">Total Orders</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.pending}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Truck className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.shipped}</div>
                <div className="text-xs text-muted-foreground">Shipped</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.delivered}</div>
                <div className="text-xs text-muted-foreground">Delivered</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={awaitingInvoiceCount > 0 ? 'ring-2 ring-orange-500' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{awaitingInvoiceCount}</div>
                <div className="text-xs text-muted-foreground">Awaiting Invoice</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold"><CurrencyValue value={stats.total_value?.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div>
                <div className="text-xs text-muted-foreground">Total Value</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Order #</th>
                  <th className="text-left p-4 font-medium">Client</th>
                  <th className="text-left p-4 font-medium">Quote #</th>
                  <th className="text-left p-4 font-medium">Amount</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Invoice</th>
                  <th className="text-left p-4 font-medium">Created</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr 
                    key={order.id} 
                    className={`border-b hover:bg-muted/50 ${
                      order.status === 'delivered' && !order.invoice_id ? 'bg-orange-50' : ''
                    }`}
                  >
                    <td className="p-4 font-medium">
                      <button 
                        onClick={() => handleViewOrder(order)}
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {order.order_number}
                        {order.status === 'delivered' && !order.invoice_id && (
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                        )}
                      </button>
                    </td>
                    <td className="p-4">{order.client_name}</td>
                    <td className="p-4 text-muted-foreground">
                      {order.quote_number || '-'}
                    </td>
                    <td className="p-4 font-medium">{currencySymbol} {order.total?.toLocaleString()}</td>
                    <td className="p-4">{getStatusBadge(order.status)}</td>
                    <td className="p-4">
                      {order.invoice_number ? (
                        <Badge variant="outline" className="text-indigo-600">
                          <Receipt className="h-3 w-3 mr-1" />
                          {order.invoice_number}
                        </Badge>
                      ) : order.status === 'delivered' ? (
                        <span className="text-orange-500 text-xs">Awaiting</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedOrder(order);
                            setDocumentViewerOpen(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedOrder(order);
                            setDocumentViewerOpen(true);
                          }}>
                            <Printer className="h-4 w-4 mr-2" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedOrder(order);
                            setDocumentViewerOpen(true);
                          }}>
                            <Mail className="h-4 w-4 mr-2" /> Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {order.status === 'confirmed' && (
                            <DropdownMenuItem onClick={() => updateStatus(order.id, 'processing')}>
                              <Package className="h-4 w-4 mr-2" /> Start Processing
                            </DropdownMenuItem>
                          )}
                          {order.status === 'processing' && (
                            <DropdownMenuItem onClick={() => updateStatus(order.id, 'shipped')}>
                              <Truck className="h-4 w-4 mr-2" /> Mark Shipped
                            </DropdownMenuItem>
                          )}
                          {order.status === 'shipped' && (
                            <DropdownMenuItem onClick={() => markAsDelivered(order.id)}>
                              <CheckCircle className="h-4 w-4 mr-2" /> Mark Delivered
                            </DropdownMenuItem>
                          )}
                          {order.status === 'delivered' && !order.invoice_id && (
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedOrder(order);
                                setCreateInvoiceDialogOpen(true);
                              }}
                              className="text-indigo-600"
                            >
                              <Receipt className="h-4 w-4 mr-2" /> Create Invoice
                            </DropdownMenuItem>
                          )}
                          {order.invoice_number && (
                            <DropdownMenuItem onClick={() => navigate('/invoices')}>
                              <ExternalLink className="h-4 w-4 mr-2" /> View Invoice
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => deleteOrder(order.id)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Sales Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery}
                  onChange={(e) => setFormData({ ...formData, expected_delivery: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Shipping Address</Label>
              <Textarea
                value={formData.shipping_address}
                onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                placeholder="Enter shipping address..."
              />
            </div>

            <div className="space-y-2">
              <Label>Line Items</Label>
              <div className="border rounded-lg p-4 space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2 text-right font-medium">
                      {currencySymbol} {(item.quantity * item.unit_price).toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
            </div>

            <div className="flex justify-end border-t pt-4">
              <div className="text-right space-y-1">
                <div>Subtotal: {currencySymbol} {calculateTotals(formData.items).subtotal.toFixed(2)}</div>
                <div>Tax (10%): {currencySymbol} {calculateTotals(formData.items).tax_amount.toFixed(2)}</div>
                <div className="text-lg font-bold">Total: {currencySymbol} {calculateTotals(formData.items).total.toFixed(2)}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!formData.client_id}>Create Order</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Sales Order - {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Document Trail */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg text-sm">
                {selectedOrder.quote_number && (
                  <>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{selectedOrder.quote_number}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </>
                )}
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-purple-500" />
                  <span className="font-medium">{selectedOrder.order_number}</span>
                </div>
                {selectedOrder.invoice_number ? (
                  <>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-indigo-500" />
                      <span className="font-medium">{selectedOrder.invoice_number}</span>
                    </div>
                  </>
                ) : selectedOrder.status === 'delivered' && (
                  <>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex items-center gap-2 text-orange-500">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Invoice Pending</span>
                    </div>
                  </>
                )}
              </div>

              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Order #</p>
                  <p className="font-semibold">{selectedOrder.order_number}</p>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Client</p>
                  <p className="font-semibold">{selectedOrder.client_name}</p>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-semibold text-primary">
                    {selectedOrder.currency} {selectedOrder.total?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h4 className="font-semibold mb-3">Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">#</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-center p-3">Qty</th>
                        <th className="text-right p-3">Unit Price</th>
                        <th className="text-right p-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3">{idx + 1}</td>
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-center">{item.quantity}</td>
                          <td className="p-3 text-right">{selectedOrder.currency} {item.unit_price?.toFixed(2)}</td>
                          <td className="p-3 text-right font-medium">{selectedOrder.currency} {item.total?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50">
                      <tr className="border-t">
                        <td colSpan={4} className="p-3 text-right font-semibold">Subtotal:</td>
                        <td className="p-3 text-right font-semibold">
                          {selectedOrder.currency} {selectedOrder.subtotal?.toFixed(2)}
                        </td>
                      </tr>
                      {selectedOrder.tax_amount > 0 && (
                        <tr>
                          <td colSpan={4} className="p-3 text-right">Tax:</td>
                          <td className="p-3 text-right">
                            {selectedOrder.currency} {selectedOrder.tax_amount?.toFixed(2)}
                          </td>
                        </tr>
                      )}
                      <tr className="border-t-2">
                        <td colSpan={4} className="p-3 text-right font-bold text-lg">Total:</td>
                        <td className="p-3 text-right font-bold text-lg text-primary">
                          {selectedOrder.currency} {selectedOrder.total?.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Dates */}
              <div className="flex justify-between text-xs text-muted-foreground border-t pt-4">
                <span>Created: {new Date(selectedOrder.created_at).toLocaleString()}</span>
                {selectedOrder.delivered_at && (
                  <span>Delivered: {new Date(selectedOrder.delivered_at).toLocaleString()}</span>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            {selectedOrder?.status === 'delivered' && !selectedOrder?.invoice_id && (
              <Button 
                onClick={() => {
                  setCreateInvoiceDialogOpen(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Receipt className="h-4 w-4 mr-2" /> Create Invoice
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={createInvoiceDialogOpen} onOpenChange={setCreateInvoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-indigo-500" />
              Create Invoice
            </DialogTitle>
            <DialogDescription>
              This will create an invoice from the delivered sales order.
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sales Order:</span>
                  <span className="font-semibold">{selectedOrder.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span className="font-semibold">{selectedOrder.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items:</span>
                  <span className="font-semibold">{selectedOrder.items?.length || 0}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground">Total Amount:</span>
                  <span className="font-semibold text-primary">
                    {selectedOrder.currency} {selectedOrder.total?.toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <Receipt className="h-5 w-5 text-indigo-600" />
                <span className="text-sm text-indigo-700">
                  A new invoice will be created with all order details. You can edit it later.
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateInvoiceDialogOpen(false)}>Cancel</Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              onClick={() => createInvoice(selectedOrder?.id)}
            >
              <Receipt className="h-4 w-4 mr-2" /> Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer for Print/Email */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        document={selectedOrder}
        type="sales_order"
        template={template}
        onEmailSent={() => fetchOrders()}
      />
    </div>
  );
};

export default SalesOrders;
